<template>
    <v-btn
        rounded="0"
        height="64"
        elevation="0"
        flat
        :active="active"
        :color="active ? 'white' : 'transparent'"
        class="search-mode-toggle__btn text-h3"
        :class="{ 'search-mode-toggle__btn--active': active }"
    >
        <div class="search-mode-toggle__btn-text">
            <v-icon v-if="active" icon="mdi-magnify" size="32" />
            <div>{{ text }}</div>
        </div>
    </v-btn>
</template>

<script setup lang="ts">
import { type VBtn } from 'vuetify/components';

interface Props extends /* @vue-ignore */ NonNullable<InstanceType<typeof VBtn>['$props']> {
    /**
     * Whether the button is active
     */
    active: boolean;

    /**
     * The text to display in the button
     */
    text: string;
}

defineProps<Props>();
</script>

<style lang="scss">
.search-mode-toggle__btn {
    --search-mode-toggle__btn-border-color: var(--v-color-grey-lighten-2);
    --search-mode-toggle__btn-color: rgb(var(--v-theme-primary));
    --search-mode-toggle__btn-inactive-color: #657785;

    position: relative;
    border-radius: 0;
    color: var(--search-mode-toggle__btn-inactive-color, #657785) !important;

    &--active {
        --v-hover-opacity: 0 !important;
        --v-activated-opacity: 0 !important;
        color: var(--search-mode-toggle__btn-color, #00aa50) !important;
        font-weight: bold !important;
    }

    &:nth-child(1) {
        border-radius: 8px 0 8px 0;

        &:before {
            content: '';
            position: absolute;
            bottom: 0;
            right: -1px;
            height: 8px;
            width: calc(100% + 1px);
            z-index: 1;
            border-style: solid;
            border-color: var(--search-mode-toggle__btn-border-color, #e6e6e6);
            border-width: 0 1px 1px 0;
            border-radius: 0 0 8px 0;
        }

        &.search-mode-toggle__btn--active {
            border-radius: 8px 8px 0 0;

            &:before {
                top: -1px;
                right: 0;
                height: calc(100% - 6px);
                width: 8px;
                border-width: 1px 1px 0 0;
                border-radius: 0 8px 0 0;
            }
        }
    }

    &:nth-child(2) {
        border-radius: 0 8px 0 8px;

        &:before {
            content: '';
            position: absolute;
            bottom: 0;
            left: -1px;
            height: 8px;
            width: calc(100% + 1px);
            z-index: 1;
            border-style: solid;
            border-color: var(--search-mode-toggle__btn-border-color, #e6e6e6);
            border-width: 0 0 1px 1px;
            border-radius: 0 0 0 8px;
        }

        &.search-mode-toggle__btn--active {
            border-radius: 8px 8px 0 0;

            &:before {
                top: -1px;
                left: 0;
                height: calc(100% - 6px);
                width: 8px;
                border-width: 1px 0 0 1px;
                border-radius: 8px 0 0 0;
            }
        }
    }

    &-text {
        border-bottom: 1px solid transparent;
        display: flex;
        align-items: center;

        .search-mode-toggle__btn--active & {
            border-color: var(--search-mode-toggle__btn-color, #00aa50);
        }
    }
}
</style>
